import { logError } from "src/config/logLevel/logger";
import { getExperiy } from "src/utils/getDates";
import {
  calculateTransferCosts,
  checkIfOverEmpty,
  checkIfShortEmpty,
  getTotalMargin,
  getUpdatedOsItemsWithCostAndMargin,
  overShortPoItemMapper
} from "src/utils/overShortUtils";
import {
  formatCurrencyOrNumber,
  formatToCurrency,
  roundNumber
} from "src/utils/genericFunctions";
import { Actions, ApiStatus } from "src/config/enums/genericEnums";
import {
  addOsItem,
  editOverItem,
  handleEditShort,
  removeOsItem
} from "src/pages/CreateOverShort/slice/CreateOsReducers";
import stringToNumber from "src/utils/stringToNumber";
import { v4 as uuidv4 } from "uuid";
import { EditOsInitialState } from "../interface";
import { matchPalletItem } from "src/utils/transferUtils";
import {
  calculateEaches,
  IGetStoreCreditDetails,
  mapPoItemsWithStoreCreditDetails
} from "src/utils/storeCreditUtils";

export const initialState: EditOsInitialState = {
  overShortItems: [],
  overShortItemsCopy: [],
  deletedPoItemIds: {},
  searchQuery: "",
  searchResults: [],
  overShortBasicData: {
    storeCreditType: "Over/Short",
    supplierOriginStore: "",
    effectiveDate: new Date().toString(),
    createdByUser: "",
    purchasingOrganization: { name: "", responseCode: "" },
    invoiceNumber: ""
  },
  notes: "",
  internalId: "",
  status: "",
  approvedOrRejectedDate: "",
  refTxfr: "",
  searchProductsLoading: false,
  getOsProductsStatus: ApiStatus.PENDING,
  margin: 0,
  totAdjustedRetail: 0,
  totAdjustedRetailForMargin: 0,
  isOverEmpty: true,
  isShortEmpty: true,
  totTransferCost: 0,
  blockNavigation: true,
  invoiceLabel: "",
  storeId: "",
  wareHouse: "",
  uploadAttachmentLoading: false,
  attachments: [],
  approverNotes: "",
  approverAttachments: [],
  referenceTransferReceivedDate: "",
  deletedAttachmentPaths: [],
  resetSort: false,
  notesCopy: "",
  approverNotesCopy: "",
  transferItems: [],
  overShortDocId: "",
  getStoreCreditDetailsStatus: "pending",
  getStoreCreditDetails: []
};

export const handlePendingForUploadAttachment = (state: any) => {
  state.uploadAttachmentLoading = true;
};

export const handleFulFillForUploadAttachment = (state: any) => {
  state.uploadAttachmentLoading = false;
};

export const handleRejectForUploadAttachment = (state: any) => {
  state.uploadAttachmentLoading = false;
};

export const handlePendingForFetchOverShortDetails = (
  state: EditOsInitialState
) => ({
  ...state,
  getOsProductsStatus: ApiStatus.PENDING
});

export const handleRejectedForFetchOverShortDetails = (state: any) => {
  state.getOsProductsStatus = ApiStatus.ERROR;
};

export const handlePendingForFetchTransferDetails = (state: any) => {
  state.getOsProductsStatus = ApiStatus.PENDING;
};

export const handleFulfilledForFetchTransferDetails = (
  state: EditOsInitialState,
  action: any
) => {
  const poItems = mapPoItemsWithStoreCreditDetails(
    action.payload?.data?.poItems,
    state,
    "O/S"
  );
  state.transferItems = updatePoitemsForFetchTransferDetails(poItems);
  state.getOsProductsStatus = ApiStatus.IDLE;
};

export const handleRejectedForFetchTransferDetails = (state: any) => {
  state.getOsProductsStatus = ApiStatus.IDLE;
};

export const updatePoitemsForFetchTransferDetails = (poItems: any) =>
  poItems?.map((poItem: any) => ({
    ...poItem,
    netPrice: formatCurrencyOrNumber(poItem?.netPrice),
    retail: formatCurrencyOrNumber(poItem?.retail),
    adjustedExtendedRetail: formatCurrencyOrNumber(
      poItem?.adjustedExtendedRetail
    ),
    transferCost: formatCurrencyOrNumber(poItem?.transferCost)
  }));

export const calculateShortFromPoItems = (poItem: any) =>
  +poItem.shortCases * +poItem?.packOrSize?.split("/")[0] +
  +poItem.shortEaches +
  "";

export const calculateOverFromPoItems = (poItem: any) =>
  +poItem.overCases * +poItem?.packOrSize?.split("/")[0] +
  +poItem.overEaches +
  "";

export const getInputDisabledDataFromPoitems = (poItem: any) => ({
  shortCases:
    poItem?.addedProduct || poItem?.isRandomWeight || poItem?.palletModId
      ? true
      : !!(+poItem?.overCases || +poItem?.overEaches),
  shortEaches: poItem?.addedProduct
    ? true
    : !!(+poItem?.overCases || +poItem?.overEaches),
  overCases:
    poItem?.isRandomWeight ||
    poItem?.palletModId ||
    !!(+poItem?.shortCases || +poItem?.shortEaches),
  overEaches: !!(+poItem?.shortCases || +poItem?.shortEaches)
});

export const getManipulatedReceivedEachesFromPoItems = (poItem: any) =>
  poItem?.isRandomWeight
    ? poItem?.received
    : (+poItem?.received || 0) * +poItem?.packOrSize?.split("/")[0];

export const manipulateFetchOverShortDetailsPoItemsAndGetUpdatedData = (
  poItems: any,
  state: EditOsInitialState
) => {
  let totTransferCost = 0;
  const updatedPoItems = poItems.map((poItem: any) => {
    const calCulatedShort = roundNumber(calculateShortFromPoItems(poItem));
    const calCulatedOver = roundNumber(calculateOverFromPoItems(poItem));
    totTransferCost = roundNumber(
      totTransferCost +
        calculateTransferCosts(
          poItem?.transferCost,
          calCulatedOver,
          calCulatedShort
        )
    );

    return {
      ...poItem,
      netPrice: formatCurrencyOrNumber(poItem?.netPrice),
      retail: formatCurrencyOrNumber(poItem?.retail),
      adjustedExtendedRetail: formatToCurrency(
        stringToNumber(poItem?.adjustedExtendedRetail)
      ),
      transferCost: formatCurrencyOrNumber(poItem?.transferCost),
      expiry: getExperiy(poItem?.expiry),
      pack: poItem?.packOrSize?.split("/")[0],
      inputDisabled: getInputDisabledDataFromPoitems(poItem),
      receivedEaches: calculateEaches(poItem, "O/S"),
      received: +poItem?.received || 0,
      initalOverCases: +poItem?.overCases || 0,
      initalOverEaches: +poItem?.overEaches || 0,
      initalShortEaches: +poItem?.shortEaches || 0,
      initalShortCases: +poItem?.shortCases || 0,
      uniqueId: uuidv4(),
      short: calCulatedShort,
      over: calCulatedOver
    };
  });
  return { updatedPoItems, totTransferCost };
};

export const handleFulfilledForFetchOverShortDetails = (
  state: any,
  action: any
) => {
  const { payload } = action;
  try {
    if (payload?.data) {
      const { data } = payload;
      state.internalId = data.internalId;
      state.totAdjustedRetail = formatCurrencyOrNumber(
        data?.totalExtendedRetail
      );
      state.margin = roundNumber(parseFloat(data?.margin || 0));
      state.overShortBasicData.createdByUser = data.createdByUser;
      state.overShortBasicData.storeCreditType = data.storeCreditType;
      state.overShortBasicData.effectiveDate = data.effectiveDate;
      state.overShortBasicData.inPalletReceivingProgram =
        data.inPalletReceivingProgram;
      state.wareHouse = data?.wareHouse;
      state.overShortBasicData.purchasingOrganization =
        data.purchasingOrganization;
      state.notes = data.notes;
      state.notesCopy = data.notes;
      state.status = data.status;
      state.referenceTransferReceivedDate = data.referenceTransferReceivedDate;
      state.approvedOrRejectedDate = data.approvedOrRejectedDate;
      state.overShortBasicData.invoiceNumber = data?.docId;
      state.overShortBasicData.supplierOriginStore = data?.supplier;
      state.invoiceLabel = data?.invoiceLabel;
      state.storeId = data?.storeId;
      state.refTxfr = data?.referenceTransfer;
      state.approverNotes = data?.approverNotes;
      state.approverNotesCopy = data?.approverNotes;
      state.approverAttachments = data?.approverAttachments;

      const poItems = mapPoItemsWithStoreCreditDetails(
        data.poItems,
        state,
        "O/S"
      );
      const { updatedPoItems, totTransferCost } =
        manipulateFetchOverShortDetailsPoItemsAndGetUpdatedData(poItems, state);
      state.overShortItems = updatedPoItems;
      state.overShortItemsCopy = [...updatedPoItems];
      state.totTransferCost = totTransferCost;
    }
  } catch (error) {
    logError(error);
  }
  checkIfOverEmpty(state);
  checkIfShortEmpty(state);
};

export const handleRemoveApproverAttachment = (
  state: any,
  action: { payload: number }
) => {
  const filterAttachments = state.approverAttachments?.filter(
    (_item: any, itemIndex: number) => itemIndex !== action?.payload
  );
  const deletedAttachment = state.attachments.find(
    (_item: any, itemIndex: number) => itemIndex === action?.payload
  );
  state.deletedAttachmentPaths.push(deletedAttachment?.fileUrl);
  state.approverAttachments = filterAttachments;
};

export const handleAddApproverAttachment = (
  state: any,
  action: {
    payload: { fileName: string; fileURL: string; fileType: string };
  }
) => {
  state.approverAttachments?.push(action.payload);
};

export const handleUpdateNotes = (state: any, action: any) => {
  state.notes = action.payload;
};

export const handleRemoveAttachment = (
  state: any,
  action: { payload: number }
) => {
  const filterAttachments = state.attachments.filter(
    (_item: any, itemIndex: number) => itemIndex !== action?.payload
  );
  state.attachments = filterAttachments;
};

export const handleAddAttachment = (
  state: any,
  action: {
    payload: { fileName: string; fileURL: string; fileType: string };
  }
) => {
  state.attachments.push(action.payload);
};

export const handleUpdateOSnavigationBlock = (
  state: EditOsInitialState,
  action: any
) => {
  state.blockNavigation = action.payload;
};

export const handleEnableSort = (state: EditOsInitialState, action: any) => {
  state.resetSort = action.payload;
};

export const handleUpdateOsNotes = (
  state: EditOsInitialState,
  action: { payload: string }
) => {
  state.notes = action.payload;
};

export const handleUpdateApproverNotes = (
  state: EditOsInitialState,
  action: { payload: string }
) => {
  state.approverNotes = action.payload;
};

export const handleEditShortItems = (state: EditOsInitialState, action: any) =>
  handleEditShort(state, action, Actions.EDIT);

export const handleEditOver = (state: EditOsInitialState, action: any) =>
  editOverItem(state, action, Actions.EDIT);

export const handleRemoveOSitem = (
  state: EditOsInitialState,
  action: { payload: { selectedItems: string[] } }
) => removeOsItem(state, action, Actions.EDIT);

export const handleAddOSitems = (
  state: EditOsInitialState,
  action: { payload: { item: any } }
) => addOsItem(state, action, Actions.EDIT);

export const handleCombineOsAndTransferItems = (state: EditOsInitialState) => {
  const osItems = state.overShortItemsCopy;

  const transferItemsNotInOs = state.transferItems.filter(
    (item) =>
      osItems.findIndex((osItem) => matchPalletItem(item, osItem)) === -1
  );

  const formattedTransferItems = transferItemsNotInOs.map((item) =>
    overShortPoItemMapper(item)
  );

  state.overShortItemsCopy = [...osItems, ...formattedTransferItems];
  state.overShortItems = [...osItems, ...formattedTransferItems];
};

export const handleResetEditOS = () => initialState;

export const handleSelectAllPalletItems = (
  state: EditOsInitialState,
  action: {
    payload: { palletId: string; selectedState: boolean; headerId: string };
  }
) => {
  const { palletId, selectedState, headerId } = action.payload;

  const { updatedOsItems, sum, totTransferCost, totalSumForDefaultItems } =
    getUpdatedOsItemsWithCostAndMargin(
      state.overShortItemsCopy,
      palletId,
      headerId,
      selectedState
    );

  state.overShortItemsCopy = [...updatedOsItems];
  state.overShortItems = [...updatedOsItems];
  state.totAdjustedRetail = sum;
  state.totTransferCost = totTransferCost;
  state.isOverEmpty = checkIfOverEmpty(state);
  state.isShortEmpty = checkIfShortEmpty(state);
  state.margin = getTotalMargin(
    state,
    totalSumForDefaultItems,
    totTransferCost
  );
};

export const handleGetStoreCreditDetailsForOSPending = (
  state: EditOsInitialState
) => {
  state.getStoreCreditDetailsStatus = ApiStatus.PENDING;
};

export const handleGetStoreCreditDetailsForOSRejected = (
  state: EditOsInitialState
) => {
  state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
};

export const handleGetStoreCreditDetailsForOSFulfilled = (
  state: EditOsInitialState,
  {
    payload
  }: { payload: { data: { docId: string; poItems: IGetStoreCreditDetails[] } } }
) => {
  if (payload?.data) {
    state.overShortDocId = payload.data?.docId;
    state.getStoreCreditDetails = payload.data?.poItems;
    state.getStoreCreditDetailsStatus = ApiStatus.IDLE;
  } else {
    state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
  }
};
